<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import MusicPlayer from '@src/partials/music-player'
import NavSidebar from '@components/navigation/nav-sidebar'
import AppTopBar from '@components/navigation/app-top-bar'
import AppFooter from '@components/navigation/app-footer'
import PhoneTopBar from '@components/navigation/phone-top-bar'
import TabBar from '@components/navigation/tab-bar'
import ConsentBanner from '@components/consent-banner'
import ClientOnly from '@components/client-only'
import { useAppearanceStore } from '@state/stores/appearance'
import { useNavigationStore } from '@state/stores/navigation'
import { useMediaQuery } from '@composables/use-media-query'
import { useKeyboardOpen } from '@composables/use-keyboard-open'
import { MEDIA_DESKTOP_UP, MEDIA_TABLET_UP } from '@design/breakpoints'
import consentService from '@services/consent-service'

defineProps({
    hideTopBar: {
        type: Boolean,
        default: false,
    },
    hiddenOverflow: {
        type: Boolean,
        default: false,
    },
})

const appearanceStore = useAppearanceStore()
const navigationStore = useNavigationStore()
// false on the server and in jsdom → the phone path. Story 4 carry: SSR must pick the
// tier from a request hint, or hydration will mismatch the whole shell on desktop.
const isTabletUp = useMediaQuery(MEDIA_TABLET_UP)
const isDesktop = useMediaQuery(MEDIA_DESKTOP_UP)
const isKeyboardOpen = useKeyboardOpen()

const container = ref(null)
const showConsentBanner = ref(false)

// Tablet = rail locked (labels under icons); desktop = the remembered choice (PRD rules 6/11).
const sidebarLayout = computed(() => {
    if (!isDesktop.value) return 'tablet'
    if (navigationStore.isSidebarCollapsed) return 'rail'
    return 'expanded'
})
const isCollapsed = computed(() => sidebarLayout.value !== 'expanded')

function handleWindowScroll() {
    appearanceStore.setScroll(window.pageYOffset)
}

// A real #fragment jump would run a router navigation (duplicate page view, guard rerun);
// the href stays as the no-JS fallback.
function skipToContent() {
    container.value?.focus({ preventScroll: true })
    container.value?.scrollIntoView?.({ block: 'start', behavior: 'instant' })
}

function hideConsentBanner() {
    showConsentBanner.value = false
}

onMounted(() => {
    appearanceStore.setScrollChild(container.value)
    window.addEventListener('scroll', handleWindowScroll, { passive: true })
    showConsentBanner.value = consentService.shouldShowBanner()
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleWindowScroll)
})
</script>

<template>
    <div class="main-layout">
        <a
            class="skip-link"
            href="#main-content"
            @click.prevent="skipToContent"
        >Skip to content</a>

        <!-- Phone (<768): wordmark + Log In / avatar above the shell. From 768 up the top bar lives inside .main. -->
        <PhoneTopBar
            v-if="!isTabletUp && !hideTopBar"
            class="phone-top-bar-slot"
        />

        <div :class="['shell', { 'shell-collapsed': isCollapsed }]">
            <NavSidebar
                v-if="isTabletUp"
                class="sidebar"
                :layout="sidebarLayout"
                @toggle="navigationStore.toggleSidebar()"
            />

            <div class="main">
                <AppTopBar
                    v-if="isTabletUp && !hideTopBar"
                    class="top-bar-slot"
                />
                <div
                    id="main-content"
                    ref="container"
                    class="container"
                    :class="{ 'hidden-overflow': hiddenOverflow }"
                    tabindex="-1"
                >
                    <slot />
                </div>
                <AppFooter class="footer-slot" />
            </div>
        </div>

        <!-- Phone: player slot (Slice 3 owns its look) + tab row + safe area share one glass
             surface, hidden while the soft keyboard is up. -->
        <div
            v-if="!isTabletUp"
            v-show="!isKeyboardOpen"
            class="bottom-group"
        >
            <ClientOnly>
                <MusicPlayer />
            </ClientOnly>
            <TabBar />
        </div>
        <ClientOnly v-else>
            <MusicPlayer />
        </ClientOnly>

        <ConsentBanner
            v-if="showConsentBanner"
            @close="hideConsentBanner"
        />
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.skip-link {
    position: absolute;
    top: $space-8;
    left: $space-8;
    z-index: $layer-modal-z-index;
    padding: $space-8 $space-12;
    color: $c-text-primary;
    background: $c-surface-overlay;
    border-radius: $radius-8;
    transform: translateY(-200%);

    &:focus {
        transform: none;
    }
}

// ---- phone (<768): phone top bar above the shell, glass bottom group over the page -------
.phone-top-bar-slot {
    position: sticky;
    top: 0;
    z-index: $layer-dropdown-z-index;
}

.shell {
    display: grid;
    min-height: calc(100vh - #{$size-phone-top-bar-height} - env(safe-area-inset-top));
}

.main {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    // Clears the fixed bottom group (tab row + player slot + home indicator); the tablet
    // tier overrides this to the player alone.
    padding-bottom: calc(#{$size-tab-bar-height} + #{$size-music-player} + env(safe-area-inset-bottom));
    // `clip`, not `hidden`: hidden makes .main a scroll container and unsticks the top bar.
    overflow-x: clip;
}

// No percentage min-heights in this column: they resolve against the stretched grid track,
// so the children overflow .main and shrink (top bar 40px, footer under the player).
.container {
    flex: 1 0 auto;
    outline: none;
    // The skip link renders at every tier; clear the sticky phone top bar here (the tablet
    // tier swaps in the shell top bar's height below).
    scroll-margin-top: $size-phone-top-bar-height;
}

.top-bar-slot,
.footer-slot {
    flex-shrink: 0;
}

// T5 glass on the whole bottom group (phone amendment 2026-09-10; the iOS app is the
// precedent for glass on the tab bar). Resting labels are text/tertiary by design-review
// exception — the contrast receipt is in the Figma notes (528:1372).
.bottom-group {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: $layer-overlay-z-index;
    // Side insets too: a landscape phone under viewport-fit=cover has the notch at one side;
    // the glass still runs edge to edge.
    padding-right: env(safe-area-inset-right);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);

    @include glass;

    // The player partial is position: fixed on its own; inside the group it stacks. The
    // layout's scope id is stamped on the child's root, so this scoped rule reaches it. The
    // player still paints its own opaque bar (glass shows under the tab row only until Slice 3),
    // and its own side-inset padding nests inside the group's (landscape phones <768 only).
    .music-player {
        position: static;
    }

    @media (prefers-reduced-transparency: reduce) {
        background: $c-surface-chrome;
        backdrop-filter: none;
    }
}

// ---- tablet + desktop: sidebar column + main column ---------------------
// `--shell-sidebar-width` drives the column and stays public (D12) for anything that needs the
// sidebar width — but prefer percentages of the content column over viewport math.
@include tablet {
    .shell {
        --shell-sidebar-width: #{$size-sidebar-rail};

        grid-template-columns: var(--shell-sidebar-width) minmax(0, 1fr);
        min-height: 100vh;

        @include transition(grid-template-columns, slow);
    }

    // Player bar (Slice 3) runs full width under sidebar and content → viewport − player.
    // Sticky makes the sidebar a stacking context: lift it to the tooltip layer so the
    // rail tooltips paint over the page (still under player 7 · top bar 8 · popover 9).
    .sidebar {
        position: sticky;
        top: 0;
        z-index: $layer-tooltip-z-index;
        height: calc(100vh - #{$size-music-player});
    }

    .main {
        min-height: 100vh;
        padding-bottom: $size-music-player;
    }

    .top-bar-slot {
        position: sticky;
        top: 0;
        z-index: $layer-dropdown-z-index;
    }

    .container {
        padding-top: $space-16;
        // A native #main-content jump (no JS) also clears the sticky top bar.
        scroll-margin-top: $size-top-bar-height;

        &.hidden-overflow {
            overflow: hidden;
        }
    }
}

@include desktop {
    .shell {
        --shell-sidebar-width: #{$size-sidebar-expanded};

        &.shell-collapsed {
            --shell-sidebar-width: #{$size-sidebar-rail};
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .shell {
        transition: none;
    }
}
</style>
